import { Fragment, useState } from 'react'
import {
  Title, Group, Card, Stack, Text, TextInput, Textarea, Button, NumberInput,
  Table, Badge, ActionIcon, Modal, LoadingOverlay, MultiSelect, Switch,
  Select,
} from '@mantine/core'
import {
  IconPlus, IconPencil, IconTrash, IconPhoto,
  IconEye, IconEyeOff,
} from '@tabler/icons-react'
import { useForm, Controller, useFieldArray } from 'react-hook-form'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'
import { confirmDelete } from '@/lib/confirm'
import { useDoctors, useDepartments } from '@/lib/swr'
import type { Doctor, Department, ExperienceEntry, EducationEntry } from '@/types'

interface DoctorFormData {
  name: string
  badge: string
  role: string
  description: string
  specialties: { value: string }[]
  schedule: string
  experience: string
  experienceHistory: ExperienceEntry[]
  education: EducationEntry[]
  yearsExp: number
  departments: string[]
  isHead: boolean
  profileId: string
  image: string
}

const EMPTY_FORM: DoctorFormData = {
  name: '', badge: '', role: '', description: '',
  specialties: [],
  schedule: '', experience: '',
  experienceHistory: [],
  education: [],
  yearsExp: 0,
  departments: [],
  isHead: false,
  profileId: '', image: '',
}

export default function DoctorList() {
  const { data: doctorsData, isLoading, mutate } = useDoctors({ limit: '100' })
  const { data: deptsData } = useDepartments()
  const doctors: Doctor[] = doctorsData?.data ?? []
  const departments: Department[] = (deptsData as any)?.data ?? []

  const [modalOpen, setModalOpen] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const [uploadingImage, setUploadingImage] = useState(false)
  const [badgeFilter, setBadgeFilter] = useState<string | null>(null)

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<DoctorFormData>({ defaultValues: EMPTY_FORM })

  const { fields: expFields, append: appendExp, remove: removeExp } = useFieldArray({ control, name: 'experienceHistory' })
  const { fields: eduFields, append: appendEdu, remove: removeEdu } = useFieldArray({ control, name: 'education' })

  const watchImage = watch('image')

  const deptOptions = departments.map((d) => ({ value: d._id, label: d.name }))

  const BADGE_UNSPECIFIED = 'Тодорхойгүй'
  const getBadgeKey = (doc: Doctor) => (doc.badge?.trim() ? doc.badge.trim() : BADGE_UNSPECIFIED)

  const badgeOptions = Array.from(new Set(doctors.map(getBadgeKey))).sort((a, b) => a.localeCompare(b, 'mn'))

  const filteredDoctors = badgeFilter ? doctors.filter((d) => getBadgeKey(d) === badgeFilter) : doctors

  const groupedDoctors = badgeOptions
    .map((badge) => ({ badge, items: filteredDoctors.filter((d) => getBadgeKey(d) === badge) }))
    .filter((g) => g.items.length > 0)

  const getDeptName = (dept: string | Department) => {
    if (typeof dept === 'object' && dept) return dept.name
    const found = departments.find((d) => d._id === dept)
    return found?.name ?? '—'
  }

  const getFirstDeptColor = (depts: (string | Department)[]) => {
    if (!depts?.length) return '#006C67'
    const first = depts[0]
    if (typeof first === 'object' && first) return first.color
    const found = departments.find((d) => d._id === first)
    return found?.color ?? '#006C67'
  }

  const openCreate = () => {
    setEditId(null)
    reset(EMPTY_FORM)
    setModalOpen(true)
  }

  const openEdit = (doc: Doctor) => {
    setEditId(doc._id)
    const deptIds = (doc.departments || []).map((d) => typeof d === 'object' ? d._id : d)
    reset({
      name: doc.name,
      badge: doc.badge, role: doc.role, description: doc.description,
      specialties: (doc.specialties || []).map((s) => ({ value: s })),
      schedule: doc.schedule, experience: doc.experience,
      experienceHistory: doc.experienceHistory || [],
      education: doc.education || [],
      yearsExp: doc.yearsExp || 0,
      departments: deptIds,
      isHead: doc.isHead || false,
      profileId: doc.profileId || '', image: doc.image,
    })
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    reset(EMPTY_FORM)
  }

  const onSubmit = handleSubmit(async (form) => {
    if (!form.departments.length) {
      toast.error('Тасаг заавал шаардлагатай')
      return
    }
    try {
      const payload = {
        ...form,
        specialties: form.specialties.map((s) => s.value),
        yearsExp: Number(form.yearsExp) || 0,
      }
      if (editId) {
        await api.admin.updateDoctor(editId, payload)
      } else {
        await api.admin.createDoctor(payload)
      }
      toast.success(editId ? 'Эмч шинэчлэгдлээ' : 'Эмч нэмэгдлээ')
      closeModal()
      mutate()
    } catch (err: any) {
      toast.error(err instanceof Error ? err.message : 'Алдаа гарлаа')
    }
  })

  const handleDelete = (id: string) => confirmDelete('Энэ эмчийг устгах уу?', async () => {
    try {
      await api.admin.deleteDoctor(id)
      toast.success('Эмч устгагдлаа')
      mutate()
    } catch (err: any) {
      toast.error(err instanceof Error ? err.message : 'Алдаа гарлаа')
    }
  })

  const toggleStatus = async (doc: Doctor) => {
    try {
      await api.admin.updateDoctorStatus(doc._id, !doc.isActive)
      mutate()
    } catch (err: any) {
      toast.error(err instanceof Error ? err.message : 'Алдаа гарлаа')
    }
  }

  const handleImageUpload = async (file: File) => {
    setUploadingImage(true)
    try {
      const result = await api.admin.uploadImage(file)
      setValue('image', result.url, { shouldDirty: true })
      toast.success('Зураг байршуулагдлаа')
    } catch {
      toast.error('Зураг байршуулахад алдаа гарлаа')
    } finally {
      setUploadingImage(false)
    }
  }

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={2}>Эмч нар</Title>
        <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>Эмч нэмэх</Button>
      </Group>

      <Card withBorder pos="relative">
        <LoadingOverlay visible={isLoading} />
        <Group mb="sm" justify="space-between">
          <Select
            label="Албан тушаалаар шүүх"
            placeholder="Бүгд"
            data={badgeOptions.map((b) => ({ value: b, label: b }))}
            value={badgeFilter}
            onChange={setBadgeFilter}
            clearable
            w={260}
          />
          <Text size="xs" c="dimmed">
            Нийт: {filteredDoctors.length} / {doctors.length}
          </Text>
        </Group>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Зураг</Table.Th>
              <Table.Th>Нэр</Table.Th>
              <Table.Th>Тасаг</Table.Th>
              <Table.Th>Зэрэг</Table.Th>
              <Table.Th>Туршлага</Table.Th>
              <Table.Th>Статус</Table.Th>
              <Table.Th w={120}>Үйлдэл</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {groupedDoctors.map((group) => (
              <Fragment key={group.badge}>
                <Table.Tr style={{ backgroundColor: '#F4F7F6' }}>
                  <Table.Td colSpan={7}>
                    <Group gap={8}>
                      <Badge variant="filled" color="teal" size="sm">{group.badge}</Badge>
                      <Text size="xs" c="dimmed">{group.items.length} хүн</Text>
                    </Group>
                  </Table.Td>
                </Table.Tr>
                {group.items.map((doc) => (
                  <Table.Tr key={doc._id}>
                    <Table.Td>
                      {doc.image ? (
                        <img src={doc.image} alt="" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }} />
                      ) : (
                        <div style={{ width: 36, height: 36, borderRadius: '50%', background: getFirstDeptColor(doc.departments), display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 14, fontWeight: 700 }}>
                          {doc.name.charAt(0)}
                        </div>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" fw={600}>{doc.name}</Text>
                      <Text size="xs" c="dimmed">{doc.badge}{doc.isHead && ' · Эрхлэгч'}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        {(doc.departments || []).map((d, i) => (
                          <Badge key={i} variant="light" size="sm" color="teal">{getDeptName(d)}</Badge>
                        ))}
                      </Group>
                    </Table.Td>
                    <Table.Td><Text size="xs">{doc.role}</Text></Table.Td>
                    <Table.Td><Text size="xs">{doc.experience}</Text></Table.Td>
                    <Table.Td>
                      <Badge color={doc.isActive ? 'green' : 'gray'} variant="light" size="sm">
                        {doc.isActive ? 'Идэвхтэй' : 'Идэвхгүй'}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        <ActionIcon variant="light" size="sm" onClick={() => toggleStatus(doc)}>
                          {doc.isActive ? <IconEye size={14} /> : <IconEyeOff size={14} />}
                        </ActionIcon>
                        <ActionIcon variant="light" size="sm" onClick={() => openEdit(doc)}>
                          <IconPencil size={14} />
                        </ActionIcon>
                        <ActionIcon variant="light" color="red" size="sm" onClick={() => handleDelete(doc._id)}>
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Fragment>
            ))}
            {filteredDoctors.length === 0 && !isLoading && (
              <Table.Tr><Table.Td colSpan={7}><Text ta="center" c="dimmed" py="lg">Эмч бүртгэгдээгүй байна</Text></Table.Td></Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Card>

      {/* Create/Edit Modal */}
      <Modal opened={modalOpen} onClose={closeModal} title={editId ? 'Эмч засах' : 'Шинэ эмч'} size="lg">
        <form onSubmit={onSubmit}>
          <Stack>
            <Controller
              name="name"
              control={control}
              rules={{ validate: (v) => (v.trim() ? true : 'Нэр шаардлагатай') }}
              render={({ field }) => <TextInput label="Нэр *" {...field} error={errors.name?.message} />}
            />
            <Controller
              name="departments"
              control={control}
              render={({ field }) => (
                <MultiSelect
                  label="Тасгууд *"
                  data={deptOptions}
                  value={field.value}
                  onChange={(v) => field.onChange(v)}
                  searchable
                  placeholder="Тасаг сонгох"
                />
              )}
            />
            <Controller
              name="isHead"
              control={control}
              render={({ field }) => (
                <Switch label="Тасгийн эрхлэгч" checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />
              )}
            />
            <Group grow>
              <Controller
                name="badge"
                control={control}
                render={({ field }) => <TextInput label="Алба тушаал" placeholder="жнь: Тасгийн эрхлэгч" {...field} />}
              />
              <Controller
                name="role"
                control={control}
                render={({ field }) => <TextInput label="Зэрэг" placeholder="жнь: АУ-ны Магистрант" {...field} />}
              />
            </Group>
            <Controller
              name="description"
              control={control}
              render={({ field }) => <Textarea label="Тайлбар" {...field} minRows={2} />}
            />
            <Group grow>
              <Controller
                name="experience"
                control={control}
                render={({ field }) => <TextInput label="Туршлага (текст)" placeholder="жнь: 15 жил" {...field} />}
              />
              <Controller
                name="yearsExp"
                control={control}
                render={({ field }) => (
                  <NumberInput
                    label="Туршлага (жил)"
                    placeholder="жнь: 15"
                    value={field.value}
                    onChange={(v) => field.onChange(Number(v) || 0)}
                    min={0}
                  />
                )}
              />
              <Controller
                name="schedule"
                control={control}
                render={({ field }) => {
                  const parts = (field.value || '').split('-').map((s: string) => s.trim())
                  const startTime = parts[0] || ''
                  const endTime = parts[1] || ''
                  const timeOptions = Array.from({ length: 28 }, (_, i) => {
                    const h = Math.floor(i / 2) + 7
                    const m = i % 2 === 0 ? '00' : '30'
                    const t = `${h.toString().padStart(2, '0')}:${m}`
                    return { value: t, label: t }
                  })
                  return (
                    <Group grow>
                      <Select
                        label="Эхлэх цаг"
                        data={timeOptions}
                        value={startTime}
                        onChange={(v) => field.onChange(`${v || ''}-${endTime}`)}
                        placeholder="09:00"
                        clearable
                      />
                      <Select
                        label="Дуусах цаг"
                        data={timeOptions}
                        value={endTime}
                        onChange={(v) => field.onChange(`${startTime}-${v || ''}`)}
                        placeholder="17:00"
                        clearable
                      />
                    </Group>
                  )
                }}
              />
            </Group>
            <Controller
              name="profileId"
              control={control}
              render={({ field }) => <TextInput label="Profile ID" placeholder="URL-д ашиглах (жнь: munkhzul)" {...field} />}
            />

            {/* Image upload */}
            <div>
              <Text size="sm" fw={500} mb={4}>Зураг</Text>
              <Group>
                <Controller
                  name="image"
                  control={control}
                  render={({ field }) => <TextInput flex={1} placeholder="URL" {...field} />}
                />
                <Button
                  size="sm" variant="light" leftSection={<IconPhoto size={14} />}
                  loading={uploadingImage}
                  onClick={() => {
                    const input = document.createElement('input')
                    input.type = 'file'
                    input.accept = 'image/*'
                    input.onchange = () => input.files?.[0] && handleImageUpload(input.files[0])
                    input.click()
                  }}
                >
                  Upload
                </Button>
              </Group>
              {watchImage && <img src={watchImage} alt="" style={{ marginTop: 8, maxHeight: 80, borderRadius: 8, objectFit: 'cover' }} />}
            </div>

            {/* Experience History */}
            <div>
              <Group justify="space-between" mb={4}>
                <Text size="sm" fw={500}>Ажлын туршлага ({expFields.length})</Text>
                <Button size="xs" variant="subtle" onClick={() => appendExp({ period: '', title: '', org: '', desc: '' })}>+ Нэмэх</Button>
              </Group>
              {expFields.map((ef, i) => (
                <Group key={ef.id} mb={4} wrap="nowrap">
                  <Controller
                    name={`experienceHistory.${i}.period` as const}
                    control={control}
                    render={({ field }) => (
                      <TextInput w={160} size="xs" placeholder="Хугацаа" {...field} />
                    )}
                  />
                  <Controller
                    name={`experienceHistory.${i}.title` as const}
                    control={control}
                    render={({ field }) => (
                      <TextInput flex={1} size="xs" placeholder="Албан тушаал" {...field} />
                    )}
                  />
                  <Controller
                    name={`experienceHistory.${i}.org` as const}
                    control={control}
                    render={({ field }) => (
                      <TextInput flex={1} size="xs" placeholder="Байгууллага" {...field} />
                    )}
                  />
                  <Controller
                    name={`experienceHistory.${i}.desc` as const}
                    control={control}
                    render={({ field }) => (
                      <TextInput flex={1} size="xs" placeholder="Тайлбар" value={field.value ?? ''} onChange={field.onChange} onBlur={field.onBlur} name={field.name} ref={field.ref} />
                    )}
                  />
                  <ActionIcon color="red" variant="light" size="sm" onClick={() => removeExp(i)}><IconTrash size={14} /></ActionIcon>
                </Group>
              ))}
            </div>

            {/* Education */}
            <div>
              <Group justify="space-between" mb={4}>
                <Text size="sm" fw={500}>Боловсрол ({eduFields.length})</Text>
                <Button size="xs" variant="subtle" onClick={() => appendEdu({ year: '', degree: '', school: '' })}>+ Нэмэх</Button>
              </Group>
              {eduFields.map((ef, i) => (
                <Group key={ef.id} mb={4}>
                  <Controller
                    name={`education.${i}.year` as const}
                    control={control}
                    render={({ field }) => (
                      <TextInput w={80} size="xs" placeholder="Он" {...field} />
                    )}
                  />
                  <Controller
                    name={`education.${i}.degree` as const}
                    control={control}
                    render={({ field }) => (
                      <TextInput flex={1} size="xs" placeholder="Зэрэг (жнь: PhD, Сэргэн засах)" {...field} />
                    )}
                  />
                  <Controller
                    name={`education.${i}.school` as const}
                    control={control}
                    render={({ field }) => (
                      <TextInput flex={1} size="xs" placeholder="Сургууль" {...field} />
                    )}
                  />
                  <ActionIcon color="red" variant="light" size="sm" onClick={() => removeEdu(i)}><IconTrash size={14} /></ActionIcon>
                </Group>
              ))}
            </div>

            <Group justify="flex-end">
              <Button variant="light" onClick={closeModal} disabled={isSubmitting}>Болих</Button>
              <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>{editId ? 'Хадгалах' : 'Нэмэх'}</Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Stack>
  )
}
